import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DateFormats } from "app/constants/dateConst";
import styles from "./ControlRecordsTable.module.scss";
import { hasPermission } from "app/components/Permission/Permission";
import { PermissionEnum } from "app/constants/permissionConst";
import Table, { Column } from "app/components/Table/Table";
import { TableProps } from "antd/es/table";
import { OrderByDef } from "app/types/apiType";
import _camelCase from "lodash/camelCase";
import _upperFirst from "lodash/upperFirst";
import { message } from "antd";
import { useSaveData } from "app/hooks/hooks";
import cx from "classnames";
import {
  TestRecordDTO,
  GroupedTestRecordTestRecordDTO,
  TestRecordsApiV2TestRecordsIdAnnotationPatchRequest,
  MeasurementMethod,
} from "@generated/v2";
import { testRecordsPatchAnnotation } from "app/api/v2/testRecordsApi";
import AnnotationActions from "app/screens/TestRecordsScreen/components/AnnotationActions/AnnotationActions";
import { AnnotationActionsEnum } from "app/constants/annotationConst";
import { getTestResultTranslationKey } from "app/helpers/utilHelper";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { columnConfig } from "app/constants/testRecordsConst";
import { useTestRecords } from "app/state/testRecords";
import { cloneDeep } from "lodash";
import { useLocation } from "react-router-dom";
import qs from "query-string";
import { ReactComponent as IconFile } from "app/assets/ch-file.svg";

interface IProps extends TableProps<GroupedTestRecordTestRecordDTO> {
  redirectTo: (id?: number, path?: string) => void;
  orderBy?: OrderByDef;
  onUpdateRecord: () => void;
}

const ControlRecordsTable = ({
  redirectTo,
  orderBy,
  onUpdateRecord,
  ...rest
}: IProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [enabledColumnNames, setEnabledColumnNames] = useState([]);
  const { views } = useTestRecords();

  const currentSearch = qs.parse(location.search);
  const viewId = Number(currentSearch?.viewId);

  /** Hook for updating record annotation*/
  const {
    /** For checking which record is updating */
    body: requestBody,
    data,
    isSuccess,
    isError,
    loading,
    setBody: setAnnotation,
  } = useSaveData<any, TestRecordsApiV2TestRecordsIdAnnotationPatchRequest>(
    testRecordsPatchAnnotation
  );

  /** Listens for test records deletion success, or error */
  useEffect(() => {
    if (isSuccess) {
      /** If success then check if tried to delete record or annotate,
       * and then show corresponding success message
       */
      message.success(
        t(
          `testRecords.${
            data?.annotation === AnnotationActionsEnum.DELETED
              ? "delete"
              : "annotation"
          }Success`
        )
      );
      onUpdateRecord();
    } else if (isError) {
      /** If error then show corresponding message
       */
      message.error(
        t(
          `testRecords.${
            data?.annotation === AnnotationActionsEnum.DELETED
              ? "delete"
              : "annotation"
          }Error`
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  // Filter enabled columns and sort by order
  const handleColumns = columns => {
    const enabledColumnNames = columns
      .filter(col => col.enabled)
      .sort((a, b) => a.order - b.order)
      .map(col => col.label);
    setEnabledColumnNames(enabledColumnNames);
  };

  useEffect(() => {
    if (viewId) {
      const view = views?.views?.find(view => view.id === viewId);
      const columns = cloneDeep(view?.columns);
      if (columns && Array.isArray(columns)) {
        handleColumns(columns);
      }
    } else {
      handleColumns(columnConfig);
    }
  }, [viewId, views]);

  // Function to render columns based on configuration
  const renderColumn = (columnName: string) => {
    switch (columnName) {
      case "measurementMethod":
        return (
          <Column
            key="measurementMethod"
            width={150}
            dataIndex="testType.measurementMethod"
          />
        );
      case "annotation":
        return (
          <Column
            key="annotation"
            dataIndex="annotation"
            width={170}
            render={text =>
              text ? t(`annotations.type${_upperFirst(text)}`) : ""
            }
          />
        );
      case "rawQR":
        return <Column key="rawQR" dataIndex="rawQR" width={250} />;
      case "batchNumber":
        return <Column key="batchNumber" dataIndex="batchNumber" width={170} />;
      case "concentration":
        return (
          <Column
            key="concentration"
            dataIndex="concentration"
            width={170}
            render={(_, { testType, substances }: TestRecordDTO) =>
              MeasurementMethod.Quantitative === testType.measurementMethod
                ? substances[0].level <
                  testType?.quantitativeRange.measurableRangeMin
                  ? `<${testType?.quantitativeRange.measurableRangeMin}`
                  : substances[0].level >
                    testType?.quantitativeRange.measurableRangeMax
                  ? `>${testType?.quantitativeRange.measurableRangeMax}`
                  : substances[0].level
                : ""
            }
          />
        );
      case "route":
        return <Column key="route" dataIndex="route" width={150} />;
      case "deviceType":
        return <Column key="deviceType" width={150} />;
      case "siteId":
        return hasPermission([PermissionEnum.ACCOUNTS_CUSTOMER_READ]) ? (
          <Column key="siteId" dataIndex="site.name" width={125} />
        ) : null;
      case "username":
        return (
          <Column key="user.username" dataIndex="user.username" width={170} />
        );
      case "operatorId":
        return <Column key="operatorId" dataIndex="operatorId" width={170} />;
      case "attachments":
        return (
          <Column
            key="user.attachments"
            dataIndex="user.attachments"
            width={30}
            render={(text, row: TestRecordDTO) => {
              return !!row.attachments?.length && <IconFile />;
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ErrorFallbackUI>
      <Table
        className={styles.table}
        rowClassName={record =>
          cx(styles.row, {
            [styles.isDeleted]:
              // @ts-ignore
              record.annotation === AnnotationActionsEnum.DELETED,
          })
        }
        onRow={record => ({
          onClick: () => {
            record.id && redirectTo(record.id);
          },
        })}
        scroll={{ x: 1100 }}
        pagination={false}
        showHeader={false}
        {...rest}
      >
        <Column key="fillerID" width={150} />
        <Column
          key="testDate"
          dataIndex="testDate"
          width={150}
          render={text => {
            return moment.parseZone(text).format(DateFormats.DATE_TIME);
          }}
        />
        <Column key="testTypeId" dataIndex="testType.name" width={150} />
        <Column
          key="result"
          dataIndex="result"
          width={135}
          render={(text, render: GroupedTestRecordTestRecordDTO) =>
            t(
              `testResultEnum.${_camelCase(
                getTestResultTranslationKey(text, render?.annotation)
              )}`
            )
          }
        />
        {/* Render enabled columns in order */}
        {enabledColumnNames
          .map(columnName => renderColumn(columnName))
          .filter(Boolean)}
        <Column
          key="actions"
          width={65}
          onCell={() => ({
            onClick: e => e.stopPropagation(),
          })}
          render={(text, record: TestRecordDTO) => (
            <AnnotationActions
              loading={requestBody?.id === record.id && loading}
              onEditAnnotation={setAnnotation}
              record={record}
              redirectTo={redirectTo}
            />
          )}
        />
      </Table>
    </ErrorFallbackUI>
  );
};

export default memo(ControlRecordsTable);
